/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import {
  FaRegPlusSquare,
  FaSchool,
  FaUserGraduate,
  FaUsers,
  FaUserTie,
} from "react-icons/fa";

//css
import "../dashboard/Dashboard.css";

// image
import appLogo from "../../assets/logo/blue-logo.png";

//components
import Card from "../../components/card/Card";
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import ListTitleSubtitle from "../../components/lists/listTitleSubtitle/ListTitleSubtitle";
import ButtonIcon from "../../components/buttons/buttonIcon/ButtonIcon";
import Barchart from "../../components/chart/barchart/Barchart";
import DoughnutChart from "../../components/chart/doughnut/DoughnutChart";
import StatCard from "../../components/statcard/StatCard";
import {
  empty,
  isArray,
  isObject,
  isString,
  prepareResponseData,
} from "../../Utilities/utils";

import adminAPI from "../../api/Admin";
import { Toast } from "primereact/toast";
import { useLocation } from "react-router-dom";
import BoxLoading from "../../components/skeleton/BoxLoading";
import colors from "../../config/colors";
import EmptyListResponse from "../../components/emptyDiv/EmptyListResponse";
import TableLoading from "../../components/skeleton/TableLoading";
import PaymentWrapper from "../../components/appWrapper/PaymentWrapper";

const PaymentDashboard = ({ ...props }) => {
  const [isLoading, setIsLoading] = useState(false);
  const toastTR = useRef(null);
  const location = useLocation();
  const [dashboardStats, setDashboardStats] = useState({
    students: 0,
    schools: 0,
    staff: 0,
    parents: 0,
  });
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [topSchoolsByActiveStudents, setTopSchoolsByActiveStudents] = useState(
    [],
  );
  const [recentlyRegisteredSchools, setRecentlyRegisteredSchools] = useState(
    [],
  );

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Top schools by active students",
        data: [],
        backgroundColor: "#6d4dc7d6",
        borderRadius: 12,
      },
    ],
  });

  useEffect(() => {
    getDashboardStats();
    getTopSchoolsByActiveStudents();
    getRecentlyRegisteredSchools();
  }, [location.pathname]);

  const [statBoxData, setStatBoxData] = useState([
    {
      id: 1,
      bgColor: "#f27c1b",
      title: "Basic",
      entry: dashboardStats.basic || 0,
      icon: <FaSchool className="stat_card_icon" />,
    },
    {
      id: 2,
      bgColor: "#98c38b",
      title: "Premium",
      entry: dashboardStats.premium || 0,
      icon: <FaUserGraduate className="stat_card_icon" />,
    },
    {
      id: 3,
      bgColor: "#633ccd",
      title: "Premium+",
      entry: dashboardStats.premium_plus || 0,
      icon: <FaUserTie className="stat_card_icon" />,
    },
    {
      id: 4,
      bgColor: "#e65061",
      title: "custom",
      entry: dashboardStats.custom || 0,
      icon: <FaUsers className="stat_card_icon" />,
    },
  ]);

  useEffect(() => {
    setStatBoxData((prev) =>
      prev.map((item) => ({
        ...item,
        entry:
          item.id === 1
            ? dashboardStats.schools || 0
            : item.id === 2
              ? dashboardStats.students || 0
              : item.id === 3
                ? dashboardStats.staff || 0
                : item.id === 4
                  ? dashboardStats.parents || 0
                  : 0,
      })),
    );
  }, [dashboardStats]);

  //alert functions
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR.current.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  /**
   * Get Dashboard stats
   * @returns
   */
  const getDashboardStats = async () => {
    try {
      if (!isLoading) setIsLoading(true);
      const response = await adminAPI.getDashboardStats();
      const response_data = prepareResponseData(response);
      if (empty(response_data) || empty(response_data.success)) {
        return responseDialog(
          "error",
          "Something went wrong",
          response_data?.response || "Please refresh or try again later!",
        );
      }

      setDashboardStats(
        isObject(response_data?.response)
          ? response_data.response
          : { students: 0, schools: 0, staff: 0, parents: 0 },
      );
    } catch (error) {
      return responseDialog(
        "error",
        error?.code === "ERR_NETWORK" ? "Network Error" : "Error Alert!",
        error?.message || "Something went wrong!",
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get Top School by Active Students
   * @returns
   */
  const getTopSchoolsByActiveStudents = async () => {
    try {
      if (!isChartLoading) setIsChartLoading(true);
      const response = await adminAPI.getTopSchoolsByActiveStudents();
      const response_data = prepareResponseData(response);
      if (empty(response_data) || empty(response_data.success)) {
        return responseDialog(
          "error",
          "Something went wrong",
          response_data?.response || "Please refresh or try again later!",
        );
      }

      const schoolStudents = isArray(
        response_data?.response?.topSchoolsByActiveStudents,
      )
        ? response_data.response.topSchoolsByActiveStudents
        : [];
      setTopSchoolsByActiveStudents(schoolStudents);
      setChartData({
        labels: schoolStudents.map((data) => {
          return isString(data?.school_abbr)
            ? data.school_abbr
            : isString(data?.school_name)
              ? data.school_name?.slice(0, 4)
              : data.school_name;
        }),
        datasets: [
          {
            label: "Active Students",
            data: schoolStudents.map((data) => {
              return data?.active_students_count || 0;
            }),
            backgroundColor: schoolStudents.map((data) => {
              const count = data?.active_students_count || 0;
              let colorCode = colors.primary;
              if (count < 50) {
                colorCode = colors.orange;
              }
              return colorCode;
            }),
            borderRadius: 12,
          },
        ],
      });
    } catch (error) {
      return responseDialog(
        "error",
        error?.code === "ERR_NETWORK" ? "Network Error" : "Error Alert!",
        error?.message || "Something went wrong!",
      );
    } finally {
      setIsChartLoading(false);
    }
  };

  /**
   * Get recently registered schools
   * @returns
   */
  const getRecentlyRegisteredSchools = async () => {
    try {
      if (!isLoading) setIsLoading(true);
      const response = await adminAPI.getRecentlyRegisteredSchools();
      const response_data = prepareResponseData(response);
      if (empty(response_data) || empty(response_data.success)) {
        return responseDialog(
          "error",
          "Something went wrong",
          response_data?.response || "Please refresh or try again later!",
        );
      }

      setRecentlyRegisteredSchools(
        isArray(response_data?.response) ? response_data.response : [],
      );
    } catch (error) {
      return responseDialog(
        "error",
        error?.code === "ERR_NETWORK" ? "Network Error" : "Error Alert!",
        error?.message || "Something went wrong!",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <PaymentWrapper {...props}>
        <main>
          {/* left box */}
          <div className="main_box">
            {/* stat box */}
            <div className="top_stat_box">
              {statBoxData.map((data, index) => {
                return (
                  <Card
                    key={data?.id || index}
                    children={
                      !isLoading ? (
                        <StatCard
                          key={data.id}
                          underlineWidth={String(data.entry)?.length * 18}
                          bgColor={data.bgColor}
                          title={data.title}
                          entry={data.entry}
                          icon={data.icon}
                        />
                      ) : (
                        <BoxLoading />
                      )
                    }
                    addStyle="card_adjust_stat"
                  />
                );
              })}
            </div>
            {/* stat header end */}

            {/* chart */}
            <div className="chart_box">
              <Card
                children={
                  <>
                    <MainHeader title="Top schools by active students" />
                    <Barchart data={chartData} />
                  </>
                }
                addStyle="barchart"
              />

              <div className="inner_chart_box">
                <Card
                  children={
                    <>
                      <MainHeader title="Students Stat By Gender" />
                      <DoughnutChart doughnutData={[500000, 1000000]} />
                    </>
                  }
                  id="chart_box1"
                />
                <Card
                  children={
                    <>
                      <MainHeader
                        title="Recently Registered Schools"
                        rightIcon={
                          <FaRegPlusSquare
                            style={{ color: "#389d17", cursor: "pointer" }}
                          />
                        }
                      />
                      <div className="dashboard_school_list">
                        {isArray(recentlyRegisteredSchools) &&
                          recentlyRegisteredSchools.map((school) => {
                            return !isChartLoading ? (
                              <ListTitleSubtitle
                                key={school._id}
                                img={school.school_avatar || appLogo}
                                title={school?.school_name || "N/A"}
                                subtitle={
                                  (school?.active_students_count || 0) +
                                  " Students"
                                }
                              />
                            ) : empty(topSchoolsByActiveStudents) ? (
                              <EmptyListResponse />
                            ) : (
                              <TableLoading rows={20} cols={1} header={false} />
                            );
                          })}
                        <ButtonIcon
                          buttonText="View all"
                          borderColor="#633ccd"
                          color="#633ccd"
                          marginTop={2}
                        />
                      </div>
                    </>
                  }
                  id="chart_box2"
                />
              </div>
            </div>
            {/* end chart */}
          </div>
          {/* end of left box */}
        </main>
        <Toast ref={toastTR} position="bottom-left" />
      </PaymentWrapper>
    </>
  );
};

export default PaymentDashboard;
